/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'motion/react';
import { Building2, MailOpen, MapPin } from 'lucide-react';

const cards = [
  {
    id: 'company',
    title: '会社名',
    icon: Building2,
    content: (
      <p className="text-sm md:text-base font-bold text-white leading-6">
        Meetune Entertainment株式会社
      </p>
    ),
  },
  {
    id: 'contact',
    title: '連絡先',
    icon: MailOpen,
    content: (
      <div className="space-y-2 text-sm">
        <p>
          <span className="block text-white/35 text-xs mb-1">お問い合わせ</span>
          <a href="mailto:koemori@meetune.com" className="text-white font-semibold hover:text-[#00F5D4] transition-colors">
            koemori@meetune.com
          </a>
        </p>
        <p>
          <span className="block text-white/35 text-xs mb-1">ビジネス提携</span>
          <a href="mailto:contact@meetune.com" className="text-white font-semibold hover:text-[#00F5D4] transition-colors">
            contact@meetune.com
          </a>
        </p>
      </div>
    ),
  },
  {
    id: 'address',
    title: '所在地',
    icon: MapPin,
    content: (
      <div className="text-sm text-white/60 leading-6">
        <p className="font-bold text-white">〒150-0001</p>
        <p>東京都渋谷区神宮前一丁目5番8号</p>
        <p>神宮前タワービルディング12階</p>
      </div>
    ),
  },
];

export default function AboutUs() {
  return (
    <div className="bg-[#0E1110] relative overflow-hidden">
      <section
        id="about-intro"
        className="relative min-h-[calc(100vh-72px)] snap-start flex items-center overflow-hidden bg-[#0E1110]"
      >
        <img src="/assets/about-hero.webp" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-[center_70%] opacity-45 brightness-[1.3]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050909]/95 via-[#050909]/70 to-[#050909]/45" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 -translate-y-10">
          <div className="max-w-none">
            <p className="inline-block text-xs tracking-[0.28em] font-bold text-[#00F5D4] mb-4">Koemoriについて</p>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight leading-tight text-white mb-5">新しいエンターテイメント</h2>
            <p className="max-w-2xl text-lg md:text-xl leading-8 text-white/80">
              モバイル歌唱エンターテイメントを通じて、誰もが歌う楽しさを味わい、音楽でつながる体験を提供します。
            </p>
          </div>
        </div>
      </section>

      <section id="about-company" className="relative z-20 isolate snap-start min-h-[calc(100vh-320px)] py-10 lg:py-14 flex items-center bg-[#0E1110]">
      <div className="absolute top-0 right-[18%] w-96 h-96 rounded-full bg-[#00F5D4]/5 blur-[125px] pointer-events-none" />
      <div className="absolute bottom-0 left-[10%] w-72 h-72 rounded-full bg-[#5371FF]/4 blur-[110px] pointer-events-none" />

      <div className="w-full max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            className="inline-block text-xs tracking-[0.28em] font-bold text-[#00F5D4] mb-2"
          >
            ABOUT US
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.55 }}
            className="text-3xl md:text-5xl font-black tracking-tight leading-tight text-white mb-5"
          >
            音楽で、人々をつなぐ。
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.55, delay: 0.08 }}
            className="text-sm md:text-base text-white/55 leading-6"
          >
            私たちは、一人ひとりの歌声が届けるよう、やさしくて楽しいモバイル歌唱体験を作ります。
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {cards.map((card, index) => {
            const Icon = card.icon;
            return (
              <motion.article
                key={card.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group min-h-[150px] p-5 rounded-[22px] bg-gradient-to-b from-white/[0.07] to-white/[0.025] border border-white/[0.08] hover:border-[#00F5D4]/28 hover:shadow-[0_22px_55px_rgba(0,0,0,0.35)] transition-[border-color,box-shadow] flex flex-col"
              >
                <div className="w-10 h-10 rounded-xl bg-[#00F5D4]/10 border border-[#00F5D4]/15 text-[#00F5D4] flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                  <Icon className="w-5 h-5 stroke-[1.8]" />
                </div>
                <h3 className="text-sm font-bold text-white/45 mb-2">{card.title}</h3>
                <div className="mt-auto">{card.content}</div>
              </motion.article>
            );
          })}
        </div>
      </div>
      </section>
    </div>
  );
}
